import React, {useCallback, useRef, useState} from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import Button from "@amzn/meridian/button";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {createSortPathConfiguration} from "../../resources/SortPathSettingsResource";
import {getNodeId} from "../../Utility";
import {
    parseCSV,
    parseClusterParamsCSV,
    validateRow,
    validateDuplicates,
    buildSortPathSettingFromRow,
    generateSettingName,
} from "../../helper/BulkSortGridAllocationHelper";
import { SORT_PATH_CSV_HEADERS, COLORS, ACTION_LABELS, STATUS_LABELS, DEFAULT_ERROR_MESSAGE } from "../../constants/SortPathBulkUploadConstants";

/**
 * Extracts a readable error message from an API response.
 * The errorMessage field may be a JSON string with a nested message, or a plain string.
 */
function extractErrorMessage(response) {
    if (!response) return DEFAULT_ERROR_MESSAGE;
    try {
        return JSON.parse(response.errorMessage).message;
    } catch (_) {
        return response.errorMessage || DEFAULT_ERROR_MESSAGE;
    }
}

/**
 * Renders a progress bar showing upload completion percentage.
 */
const ProgressBar = ({current, total}) => {
    const percent = Math.round((current / total) * 100);
    return (
        <Column spacing="xsmall">
            <Row alignmentHorizontal="center">
                <Text type="b300">
                    <FormattedMessage id="sortPaths.bulkUploadProgress" defaultMessage="Uploading {current}/{total}" values={{current, total}}/>
                </Text>
            </Row>
            <div
                role="progressbar"
                aria-label="Upload progress"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{width: "100%", backgroundColor: COLORS.PROGRESS_BG, borderRadius: "4px", height: "8px"}}
            >
                <div style={{
                    width: `${percent}%`,
                    backgroundColor: COLORS.PROGRESS_BAR,
                    height: "8px",
                    borderRadius: "4px",
                    transition: "width 0.3s ease",
                }}/>
            </div>
        </Column>
    );
};

/**
 * Bulk Upload modal component for Sort Grid Allocation configurations.
 * Handles CSV file selection, validation, and sequential upload of sort path settings.
 */
const BulkSortGridAllocationUpload = ({intl, onComplete, sortPathConfigurationList}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [uploadResults, setUploadResults] = useState([]);
    const [parsedRows, setParsedRows] = useState([]);
    const [rawRows, setRawRows] = useState([]);
    const [clusterParamsMap, setClusterParamsMap] = useState(null);
    const [mainFileName, setMainFileName] = useState("");
    const [clusterFileName, setClusterFileName] = useState("");
    const [isValidated, setIsValidated] = useState(false);
    const [parseErrors, setParseErrors] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({current: 0, total: 0});
    const mainFileInputRef = useRef(null);
    const clusterFileInputRef = useRef(null);

    /**
     * Resets all component state to initial values.
     */
    const resetState = useCallback(() => {
        setValidationErrors([]);
        setUploadResults([]);
        setParsedRows([]);
        setRawRows([]);
        setClusterParamsMap(null);
        setMainFileName("");
        setClusterFileName("");
        setIsUploading(false);
        setIsValidated(false);
        setParseErrors([]);
        setUploadProgress({current: 0, total: 0});
        if (mainFileInputRef.current) mainFileInputRef.current.value = "";
        if (clusterFileInputRef.current) clusterFileInputRef.current.value = "";
    }, []);

    /**
     * Closes the modal and triggers onComplete callback if any uploads succeeded.
     */
    const onCloseModal = useCallback(() => {
        if (isUploading) return;
        setIsModalOpen(false);
        if (uploadResults.some(r => r.success)) {
            onComplete();
        }
        resetState();
    }, [isUploading, uploadResults, onComplete, resetState]);

    /**
     * Reads the main configuration CSV file and parses it into raw rows.
     */
    const handleMainFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        setParseErrors([]);
        setValidationErrors([]);
        setUploadResults([]);
        setParsedRows([]);
        setRawRows([]);
        setIsValidated(false);
        setMainFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            const result = parseCSV(e.target.result);
            if (result.error) {
                setParseErrors([result.error]);
                return;
            }
            setRawRows(result.rows);
        };
        reader.readAsText(file);
    }, []);

    /**
     * Reads the optional cluster parameters CSV and parses it into a map keyed by nodeId#cycle.
     */
    const handleClusterFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        setParseErrors(prev => prev.filter(e => !e.startsWith('Cluster params:')));
        setClusterFileName(file.name);
        setIsValidated(false);
        const reader = new FileReader();
        reader.onload = (e) => {
            const result = parseClusterParamsCSV(e.target.result);
            if (result.error) {
                setParseErrors(prev => [...prev, `Cluster params: ${result.error}`]);
                return;
            }
            setClusterParamsMap(result.clusterParamsMap);
        };
        reader.readAsText(file);
    }, []);

    /**
     * Validates all parsed rows for duplicates, required fields, and algorithm constraints.
     */
    const handleValidate = useCallback(() => {
        const errors = [];
        const currentNodeId = getNodeId();
        // Check for duplicate cycle entries
        const dupErrors = validateDuplicates(rawRows);
        if (dupErrors.length > 0) {
            setValidationErrors(dupErrors);
            setIsValidated(false);
            return;
        }
        rawRows.forEach(row => {
            const error = validateRow(row, clusterParamsMap, sortPathConfigurationList, currentNodeId);
            if (error) errors.push(error);
        });

        if (errors.length > 0) {
            setValidationErrors(errors);
            setIsValidated(false);
        } else {
            setValidationErrors([]);
            setParsedRows(rawRows);
            setIsValidated(true);
        }
    }, [rawRows, clusterParamsMap, sortPathConfigurationList]);

    /**
     * Sequentially uploads each validated row as a sort path configuration.
     * Tracks success/failure per row and updates progress.
     */
    const handleUpload = useCallback(async () => {
        setIsUploading(true);
        setUploadProgress({current: 0, total: parsedRows.length});
        const results = [];

        for (let i = 0; i < parsedRows.length; i++) {
            const row = parsedRows[i];
            setUploadProgress({current: i + 1, total: parsedRows.length});
            const sortPathSetting = buildSortPathSettingFromRow(row, clusterParamsMap, sortPathConfigurationList);
            const isUpdate = !!sortPathSetting.settingId;
            try {
                // API call to create or update sort path configuration
                const response = await createSortPathConfiguration(getNodeId(), sortPathSetting);
                if (response && response.sortPathSetting) {
                    // API returned a valid setting — success
                    results.push({
                        row: row._rowNumber,
                        name: sortPathSetting.name,
                        cycle: row.cycle,
                        success: true,
                        isUpdate,
                    });
                } else {
                    // API responded but operation failed — extract error from response
                    results.push({
                        row: row._rowNumber,
                        name: sortPathSetting.name,
                        cycle: row.cycle,
                        success: false,
                        isUpdate,
                        error: extractErrorMessage(response),
                    });
                }
            } catch (error) {
                // Network/request-level failure
                results.push({
                    row: row._rowNumber,
                    name: sortPathSetting.name,
                    cycle: row.cycle,
                    success: false,
                    isUpdate,
                    error: error.toString(),
                });
            }
        }

        setUploadResults(results);
        setIsUploading(false);
    }, [parsedRows, clusterParamsMap, sortPathConfigurationList]);

    const successCount = uploadResults.filter(r => r.success).length;
    const failureCount = uploadResults.filter(r => !r.success).length;

    return (
        <>
            <Button onClick={() => setIsModalOpen(true)}>
                <FormattedMessage id="sortPaths.bulkUpload" defaultMessage="Bulk Upload CSV"/>
            </Button>

            <Modal
                title={intl.formatMessage({id: "sortPaths.bulkUploadTitle", defaultMessage: "Bulk Upload Sort Grid Allocations"})}
                open={isModalOpen}
                onClose={onCloseModal}
                closable={!isUploading}
                width="1000px"
                scrollContainer="modal"
            >
                <Column spacing="medium">
                    {/* File inputs */}
                    {uploadResults.length === 0 && (
                        <Column spacing="small">
                            <Text type="b300">
                                <FormattedMessage id="sortPaths.bulkUploadInstructions"
                                    defaultMessage="Upload a main configuration CSV. Optionally, upload a cluster parameters CSV with per-cluster settings."/>
                            </Text>

                            {/* Main CSV */}
                            <Column spacing="xsmall">
                                <Text type="h100">
                                    <FormattedMessage id="sortPaths.mainCsvLabel" defaultMessage="Main Configuration CSV (required)"/>
                                </Text>
                                <input
                                    ref={mainFileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleMainFileSelect}
                                    data-testid="bulkUploadMainFileInput"
                                />
                                {mainFileName && <Text type="b200" color="secondary">{mainFileName}</Text>}
                            </Column>

                            {/* Cluster params CSV */}
                            <Column spacing="xsmall">
                                <Text type="h100">
                                    <FormattedMessage id="sortPaths.clusterCsvLabel"
                                        defaultMessage="Cluster Parameters CSV (for STAG and AMZL sites)"/>
                                </Text>
                                <input
                                    ref={clusterFileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleClusterFileSelect}
                                    data-testid="bulkUploadClusterFileInput"
                                />
                                {clusterFileName && <Text type="b200" color="secondary">{clusterFileName}</Text>}
                            </Column>
                        </Column>
                    )}

                    {/* Parse errors */}
                    {parseErrors.length > 0 && (
                        <Alert type="error">
                            <Column spacing="xsmall">
                                {parseErrors.map((err, i) => (
                                    <Text key={i} type="b200">{err}</Text>
                                ))}
                            </Column>
                        </Alert>
                    )}

                    {/* Validation errors */}
                    {validationErrors.length > 0 && (
                        <Alert type="error">
                            <Column spacing="xsmall">
                                <Text type="h100">
                                    <FormattedMessage id="sortPaths.bulkValidationErrors" defaultMessage="Validation errors found:"/>
                                </Text>
                                {validationErrors.map((err, i) => (
                                    <Text key={i} type="b200">{err}</Text>
                                ))}
                            </Column>
                        </Alert>
                    )}

                    {/* Progress bar during upload — replaces the info alert */}
                    {isUploading && (
                        <ProgressBar current={uploadProgress.current} total={uploadProgress.total}/>
                    )}

                    {/* Parsed rows preview */}
                    {isValidated && parsedRows.length > 0 && uploadResults.length === 0 && (
                        <Column spacing="small">
                            {!isUploading && (
                                <Alert type="success">
                                    <FormattedMessage id="sortPaths.bulkRowsParsed"
                                        defaultMessage="{count} configuration(s) validated.{clusterInfo} Click Upload to proceed."
                                        values={{
                                            count: parsedRows.length,
                                            clusterInfo: clusterParamsMap
                                                ? ` Cluster params loaded for ${Object.keys(clusterParamsMap).length} cycle(s).`
                                                : "",
                                        }}/>
                                </Alert>
                            )}
                            <Table headerRows={1} showDividers>
                                <TableRow>
                                    <TableCell>Row</TableCell>
                                    <TableCell>Cycle</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Fill Algorithm</TableCell>
                                    <TableCell>Pick Algorithm</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                                {parsedRows.map(row => {
                                    const hasExisting = sortPathConfigurationList &&
                                        sortPathConfigurationList.some(c => c.nodeId === row[SORT_PATH_CSV_HEADERS.NODE_ID] && c.cycle === row[SORT_PATH_CSV_HEADERS.CYCLE]);
                                    return (
                                        <TableRow key={row._rowNumber}>
                                            <TableCell>{row._rowNumber}</TableCell>
                                            <TableCell>{row[SORT_PATH_CSV_HEADERS.CYCLE]}</TableCell>
                                            <TableCell>{generateSettingName(row[SORT_PATH_CSV_HEADERS.CYCLE], row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM])}</TableCell>
                                            <TableCell>{row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM]}</TableCell>
                                            <TableCell>{row[SORT_PATH_CSV_HEADERS.PICK_ALGORITHM]}</TableCell>
                                            <TableCell>
                                                <span style={{color: hasExisting ? COLORS.UPDATE : COLORS.CREATE, fontWeight: "bold"}}>
                                                    {hasExisting ? ACTION_LABELS.UPDATE : ACTION_LABELS.CREATE}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </Table>
                        </Column>
                    )}

                    {/* Upload results */}
                    {uploadResults.length > 0 && (
                        <Column spacing="small">
                            <Alert type={failureCount === 0 ? "success" : "warning"}>
                                <FormattedMessage id="sortPaths.bulkUploadSummary"
                                    defaultMessage="{success} succeeded, {failure} failed out of {total} configurations."
                                    values={{success: successCount, failure: failureCount, total: uploadResults.length}}/>
                            </Alert>
                            <Table headerRows={1} showDividers>
                                <TableRow>
                                    <TableCell>Row</TableCell>
                                    <TableCell>Cycle</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Error Details</TableCell>
                                </TableRow>
                                {uploadResults.map(result => (
                                    <TableRow key={result.row}>
                                        <TableCell>{result.row}</TableCell>
                                        <TableCell>{result.cycle}</TableCell>
                                        <TableCell>{result.name}</TableCell>
                                        <TableCell>
                                            <span style={{color: result.isUpdate ? COLORS.UPDATE : COLORS.CREATE, fontWeight: "bold"}}>
                                                {result.isUpdate ? ACTION_LABELS.UPDATE : ACTION_LABELS.CREATE}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span style={{color: result.success ? COLORS.SUCCESS : COLORS.FAILED, fontWeight: "bold"}}>
                                                {result.success ? STATUS_LABELS.SUCCESS : STATUS_LABELS.FAILED}
                                            </span>
                                        </TableCell>
                                        <TableCell>{result.error || "-"}</TableCell>
                                    </TableRow>
                                ))}
                            </Table>
                        </Column>
                    )}
                </Column>

                <ModalFooter>
                    <Row alignmentHorizontal="end" spacing="small">
                        <Button type="secondary" onClick={onCloseModal}>
                            <FormattedMessage id="sortPaths.close" defaultMessage="Close"/>
                        </Button>
                        {rawRows.length > 0 && !isValidated && uploadResults.length === 0 && (
                            <Button onClick={handleValidate}>
                                <FormattedMessage id="sortPaths.validate" defaultMessage="Validate"/>
                            </Button>
                        )}
                        {isValidated && parsedRows.length > 0 && uploadResults.length === 0 && !isUploading && (
                            <Button onClick={handleUpload} disabled={isUploading}>
                                <FormattedMessage id="sortPaths.upload" defaultMessage="Upload"/>
                            </Button>
                        )}
                    </Row>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default injectIntl(BulkSortGridAllocationUpload);
